import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import { FaSync, FaTimes } from "react-icons/fa";
import { ArrowLeft, CalendarDays, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { ChevronDownIcon, ChevronUpIcon } from "../../../helper/HwIcon";
import { adminApi } from "../api/adminApi";
import DateRangesEditor, { rangesOverlap, firstIncompleteRange } from "./DateRangesEditor";

// "Without transfer" options are not offered for selection in pricing.
const isWithoutTransfer = (name) =>
  String(name || "").toLowerCase().replace(/[^a-z]/g, "").includes("withouttransfer");

// Reuse the app's own icons for the dropdown chevron + clear control.
const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <span className="rsel-chevron">
      {props.selectProps.menuIsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </span>
  </components.DropdownIndicator>
);
const ClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <FaTimes className="rsel-clear" />
  </components.ClearIndicator>
);
const rsComponents = { DropdownIndicator, ClearIndicator };

const PRICE_COLS = [
  { key: "adultPrice", label: "Adult" },
  { key: "adultPrimePrice", label: "Adult (Prime)" },
  { key: "childPrice", label: "Child" },
  { key: "childPrimePrice", label: "Child (Prime)" },
  { key: "infantPrice", label: "Infant" },
  { key: "infantPrimePrice", label: "Infant (Prime)" },
];

const rowValue = (source, tourId, variationId) => `${source || ""}::${tourId || ""}::${variationId || ""}`;

// How many users to show per page in the left-hand list.
const USERS_PER_PAGE = 25;
// How many pricing rows to show per page in the edit table.
const ROWS_PER_PAGE = 25;

// Strip any minus sign so price fields can never go negative.
const nn = (v) => String(v ?? "").replace(/-/g, "");
// A price field counts as filled only when it has a value (0 is allowed).
const hasVal = (v) => v !== "" && v !== null && v !== undefined;

let rowSeq = 0;
const blankRow = () => ({
  key: `r${++rowSeq}`,
  source: "", tourId: "", tourName: "", vendor: "",
  variationId: "", variationTitle: "", cost: "",
  adultPrice: "", adultPrimePrice: "", childPrice: "",
  childPrimePrice: "", infantPrice: "", infantPrimePrice: "",
  dateRanges: [], expanded: false,
});

const vendPill = (source) => ({
  fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, whiteSpace: "nowrap", flexShrink: 0,
  background: source === "rayna" ? "#f2ecff" : "#eef1f6",
  color: source === "rayna" ? "#7a45e0" : "#5b6577",
});

const selectStyles = {
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  menu: (b) => ({ ...b, zIndex: 9999, fontSize: 12.5 }),
  menuList: (b) => ({ ...b, paddingTop: 4, paddingBottom: 4 }),
  control: (b, s) => ({
    ...b, minHeight: 36, minWidth: 240,
    borderColor: s.isFocused ? "#3264FF" : "#e2e6ee",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(50,100,255,.1)" : "none",
    "&:hover": { borderColor: "#3264FF" }, fontSize: 13,
  }),
  option: (b) => ({ ...b, fontSize: 12.5, padding: "6px 10px", lineHeight: 1.3 }),
  singleValue: (b) => ({ ...b, fontSize: 13 }),
  placeholder: (b) => ({ ...b, fontSize: 13 }),
  input: (b) => ({ ...b, fontSize: 13 }),
  valueContainer: (b) => ({ ...b, padding: "0 8px" }),
};

// Searchable country/city selects — full-width in their .fld, matching the app look.
const locSelectStyles = {
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  menu: (b) => ({ ...b, zIndex: 9999, fontSize: 13 }),
  menuList: (b) => ({ ...b, paddingTop: 4, paddingBottom: 4 }),
  control: (b, s) => ({
    ...b, minHeight: 42, width: "100%",
    borderColor: s.isFocused ? "#3264FF" : "#e2e6ee",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(50,100,255,.12)" : "none",
    "&:hover": { borderColor: "#c3ccdb" }, fontSize: 14,
    backgroundColor: s.isDisabled ? "#f4f6fa" : "#fff",
  }),
  option: (b, s) => ({
    ...b, fontSize: 13.5, padding: "8px 12px",
    backgroundColor: s.isSelected ? "#3264FF" : s.isFocused ? "#eef2ff" : "#fff",
    color: s.isSelected ? "#fff" : "#1b2439",
  }),
  singleValue: (b) => ({ ...b, fontSize: 14 }),
  placeholder: (b) => ({ ...b, fontSize: 14 }),
  input: (b) => ({ ...b, fontSize: 14 }),
  valueContainer: (b) => ({ ...b, padding: "2px 12px" }),
};

const OptionLabel = (o, meta) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
    <span style={{ fontSize: 12.5, paddingLeft: o.isVariation ? 14 : 0, color: o.isVariation ? "#5b6577" : "#1b2439", fontWeight: o.isVariation ? 400 : 600 }}>
      {o.isVariation ? `— ${o.name}` : o.name}
    </span>
    {o.vendor && meta?.context !== "value" ? <span style={vendPill(o.source)}>{o.vendor}</span> : null}
  </div>
);

const UserPricing = () => {
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE)
  const rowsOptions = useMemo(() => [25, 50, 75, 100], [])
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [selUser, setSelUser] = useState(null);
  // "list" = users table, "edit" = single-user pricing page.
  const [view, setView] = useState("list");

  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [cityId, setCityId] = useState("");

  const [catalog, setCatalog] = useState([]);
  const [priorityMap, setPriorityMap] = useState(() => new Set());
  const [rows, setRows] = useState([blankRow()]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // "See all" = ignore the country/city filter and edit every priced row for the
  // selected user across all locations. Paginated (rowPage, distinct from the
  // user-list pagination above).
  const [seeAll, setSeeAll] = useState(false);
  const [rowPage, setRowPage] = useState(1);
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [addingVariationFor, setAddingVariationFor] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroupCollapse = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const [pendingScrollKey, setPendingScrollKey] = useState(null);
  const pendingRowRef = useRef(null);
  useEffect(() => {
    if (!pendingScrollKey) return;
    if (pendingRowRef.current) pendingRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    setPendingScrollKey(null);
  }, [pendingScrollKey]);

  const flash = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  // Flatten catalog → dropdown options for a given country/city. In "see all"
  // each row uses its OWN country/city, so this is a factory, not a single memo.
  const buildOptions = useCallback(
    (ctId, cid) => {
      const c2 = Number(cid);
      const ct2 = Number(ctId);
      const inScope = (c) =>
        (!Number.isFinite(c2) || Number(c.cityId) === c2) &&
        (!Number.isFinite(ct2) || Number(c.countryId) === ct2);
      const list = [];
      catalog.filter(inScope).forEach((c) => {
        const base = {
          source: c.source,
          tourId: String(c.tourId),
          tourName: c.tourName,
          vendor: c.vendor,
          cost: c.vendorCost ?? "",
        };
        list.push({
          ...base,
          variationId: "",
          variationTitle: "",
          name: c.tourName,
          isVariation: false,
          value: rowValue(c.source, c.tourId, ""),
          label: `${c.tourName} ${c.vendor || ""}`,
        });
      });
      return list;
    },
    [catalog]
  );

  const options = useMemo(() => buildOptions(countryId, cityId), [buildOptions, countryId, cityId]);

  const childCostFor = (r) => {
    const doc = catalog.find((c) => String(c.source) === String(r.source) && String(c.tourId) === String(r.tourId));
    if (!doc) return "";
    if (r.variationId) {
      const v = (doc.variations || []).find((x) => String(x.variation_unique_id) === String(r.variationId));
      return v?.originalChild ?? "";
    }
    if (doc.vendorCostChild != null && doc.vendorCostChild !== "") return doc.vendorCostChild;
    const vs = doc.variations || [];
    const match = vs.find((x) => Number(x.originalAdult) === Number(doc.vendorCost));
    return (match ?? vs[0])?.originalChild ?? "";
  };

  const renderCostAC = (r) => {
    const dash = (v) => (v === "" || v == null ? "—" : v);
    const box = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#f6f8fc", border: "1px solid #eef1f6", borderRadius: 7, padding: "3px 8px", minWidth: 70 };
    const lbl = { fontSize: 9, fontWeight: 700, color: "#9aa4b7", letterSpacing: 0.5 };
    const val = { fontSize: 13, fontWeight: 600, color: "#354152" };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={box}><span style={lbl}>ADT</span><span style={val}>{dash(r.cost)}</span></div>
        <div style={box}><span style={lbl}>CHD</span><span style={val}>{dash(childCostFor(r))}</span></div>
      </div>
    );
  };

  // Reconstruct a react-select value straight from a row (works for any location).
  const rowTourValue = (r) => {
    if (!r.tourId || (r.variationId && r.variationId.startsWith("__NEW__"))) return null;
    return {
      value: rowValue(r.source, r.tourId, r.variationId),
      name: r.variationTitle || r.tourName,
      isVariation: !!r.variationId,
      vendor: r.vendor,
      source: r.source,
    };
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.displayName, u.username, u.email, u.userGroup].some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [users, userSearch]);

  // Client-side pagination for the user list.
  const userPageCount = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const safePage = Math.min(userPage, userPageCount);
  const pagedUsers = useMemo(
    () => filteredUsers.slice((safePage - 1) * USERS_PER_PAGE, safePage * USERS_PER_PAGE),
    [filteredUsers, safePage]
  );

  // Jump back to the first page whenever the search query changes.
  useEffect(() => {
    setUserPage(1);
  }, [userSearch]);

  const selectedCountry = countries.find((c) => String(c.countryId) === String(countryId));
  const cityOptions = selectedCountry?.cities || [];
  const selectedCity = cityOptions.find((c) => String(c.cityId) === String(cityId));

  const loadCatalog = async () => {
    const res = await adminApi("GET", "/admin/excursions/catalog");
    const items = res?.data?.data?.excursions || [];
    setCatalog(items);
    setPriorityMap(new Set(items.filter((c) => c.priority).map((c) => `${c.source}:${c.tourId}`)));
  };
  const prKey = (r) => `${r.source}:${r.tourId}`;
  const togglePriority = async (r) => {
    if (!r.tourId) return;
    const key = prKey(r);
    const next = !priorityMap.has(key);
    setPriorityMap((s) => {
      const n = new Set(s);
      next ? n.add(key) : n.delete(key);
      return n;
    });
    try {
      await adminApi("POST", "/admin/excursions/priority", { source: r.source, tourId: r.tourId, priority: next });
    } catch {
      setPriorityMap((s) => {
        const n = new Set(s);
        next ? n.delete(key) : n.add(key);
        return n;
      });
      setToast({ type: "err", text: "Could not update priority" });
    }
  };
  const loadUsers = async () => {
    const res = await adminApi("GET", "/admin/users");
    setUsers(res?.data?.data?.users || []);
  };
  const syncUsers = async () => {
    setSyncing(true);
    try {
      const res = await adminApi("POST", "/admin/users/sync");
      if (res?.data?.statusCode === 200 || res?.status === 200) {
        await loadUsers();
        flash("ok", `Synced ${res?.data?.data?.synced ?? 0} users.`);
      } else {
        flash("err", res?.data?.message || "Sync failed.");
      }
    } catch (e) {
      flash("err", "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };
  const loadLocations = async () => {
    const res = await adminApi("GET", "/admin/excursions/locations");
    const list = res?.data?.data?.countries || [];
    setCountries(list);
    if (list.length && !countryId) {
      const DUBAI = 13668;
      const country = list.find((c) => (c.cities || []).some((ci) => Number(ci.cityId) === DUBAI)) || list[0];
      setCountryId(String(country.countryId));
      const city = (country.cities || []).find((ci) => Number(ci.cityId) === DUBAI) || country.cities?.[0];
      if (city) setCityId(String(city.cityId));
    }
  };
  const loadPricing = async (uId, cId, ctId, all) => {
    if (!uId) { setRows([blankRow()]); return; }
    if (!all && (!cId || !ctId)) { setRows([blankRow()]); return; }
    const qs = all ? `?userId=${uId}` : `?userId=${uId}&countryId=${ctId}&cityId=${cId}`;
    const res = await adminApi("GET", `/admin/users/pricing${qs}`);
    const pricing = res?.data?.data?.pricing || [];
    const mapped = pricing.map((p) => ({
      key: `r${++rowSeq}`,
      source: p.source || "", tourId: String(p.tourId || ""), tourName: p.tourName || "", vendor: p.vendor || "",
      variationId: String(p.variationId || ""), variationTitle: p.variationTitle || "",
      countryId: p.countryId ?? "", countryName: p.countryName || "", cityId: p.cityId ?? "", cityName: p.cityName || "",
      cost: p.cost ?? "",
      adultPrice: p.adultPrice ?? "", adultPrimePrice: p.adultPrimePrice ?? "",
      childPrice: p.childPrice ?? "", childPrimePrice: p.childPrimePrice ?? "",
      infantPrice: p.infantPrice ?? "", infantPrimePrice: p.infantPrimePrice ?? "",
      dateRanges: (p.dateRanges || []).map((d) => ({
        startDate: d.startDate || "", endDate: d.endDate || "",
        adultPrice: d.adultPrice ?? "", adultPrimePrice: d.adultPrimePrice ?? "",
        childPrice: d.childPrice ?? "", childPrimePrice: d.childPrimePrice ?? "",
        infantPrice: d.infantPrice ?? "", infantPrimePrice: d.infantPrimePrice ?? "",
      })),
      expanded: false,
    }));
    setRows(mapped.length ? mapped : all ? [] : [blankRow()]);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadUsers(), loadLocations(), loadCatalog()]);
      } catch (e) {
        flash("err", "Failed to load data.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelected(new Set());
    setRowPage(1);
    loadPricing(selUser?.id, cityId, countryId, seeAll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selUser, countryId, cityId, seeAll]);

  const openEdit = (u) => {
    setSelUser(u);
    setView("edit");
  };
  const backToList = () => {
    setView("list");
    setSelUser(null);
    setSelected(new Set());
  };

  const setRow = (key, patch) =>
    setRows((rs) => {
      const parentRow = rs.find((r) => r.key === key);
      if (!parentRow) return rs;
      let nextRs = rs;
      const changingIdentity =
        ("tourId" in patch && patch.tourId !== parentRow.tourId) ||
        ("source" in patch && patch.source !== parentRow.source) ||
        ("countryId" in patch && patch.countryId !== parentRow.countryId) ||
        ("cityId" in patch && patch.cityId !== parentRow.cityId);
      if (changingIdentity && !parentRow.variationId && parentRow.tourId) {
        nextRs = nextRs.filter(r => !(
          r.variationId &&
          r.source === parentRow.source &&
          r.tourId === parentRow.tourId &&
          r.countryId === parentRow.countryId &&
          r.cityId === parentRow.cityId
        ));
      }
      return nextRs.map((r) => (r.key === key ? { ...r, ...patch } : r));
    });
  // Insert a fresh empty row directly under the given row index.
  const addRowAfter = (index) =>
    setRows((rs) => {
      const n = [...rs];
      n.splice(index + 1, 0, blankRow());
      return n;
    });
  const addVariationRowAfter = (index, parentOrVariationRow) =>
    setRows((rs) => {
      const n = [...rs];
      n.splice(index + 1, 0, {
        ...blankRow(),
        countryId: parentOrVariationRow.countryId,
        countryName: parentOrVariationRow.countryName,
        cityId: parentOrVariationRow.cityId,
        cityName: parentOrVariationRow.cityName,
        source: parentOrVariationRow.source,
        tourId: parentOrVariationRow.tourId,
        tourName: parentOrVariationRow.tourName,
        vendor: parentOrVariationRow.vendor,
        variationId: `__NEW__${Math.random()}`,
      });
      return n;
    });
  // Delete a single row. Unsaved rows drop instantly; saved rows go through the
  // confirm modal (which calls the delete API).
  const deleteRow = (r) => {
    if (!r.tourId) {
      setRows((rs) => {
        const left = rs.filter((x) => x.key !== r.key);
        return left.length ? left : [blankRow()];
      });
      setSelected((s) => {
        const n = new Set(s);
        n.delete(r.key);
        return n;
      });
      return;
    }
    setSelected(new Set([r.key]));
    setConfirmDelete(true);
  };
  const onSelectTour = (key, o) => {
    if (!o) { setRow(key, { source: "", tourId: "", tourName: "", vendor: "", variationId: "", variationTitle: "", cost: "" }); return; }

    const targetRow = rows.find((x) => x.key === key);
    const isDuplicate = rows.some((other) => {
      if (!other.tourId || other.key === key) return false;
      if (
        targetRow?.tourId &&
        String(other.tourId) === String(targetRow.tourId) &&
        String(other.source || "").toLowerCase() === String(targetRow.source || "").toLowerCase() &&
        (!seeAll || (String(other.countryId ?? "") === String(targetRow.countryId ?? "") && String(other.cityId ?? "") === String(targetRow.cityId ?? "")))
      ) {
        return false;
      }
      if (seeAll && targetRow) {
        if (targetRow.countryId && other.countryId && String(targetRow.countryId) !== String(other.countryId)) return false;
        if (targetRow.cityId && other.cityId && String(targetRow.cityId) !== String(other.cityId)) return false;
      }

      const sameIdAndSource =
        String(other.tourId) === String(o.tourId) &&
        String(other.source || "").toLowerCase() === String(o.source || "").toLowerCase();
      if (sameIdAndSource) return true;

      const sameVendor =
        Boolean(other.vendor && o.vendor && String(other.vendor).trim().toLowerCase() === String(o.vendor).trim().toLowerCase());
      const sameTourId = String(other.tourId) === String(o.tourId);
      if (sameTourId && sameVendor) return true;

      const otherName = (other.tourName || "").trim().toLowerCase();
      const optName = (o.tourName || o.name || "").trim().toLowerCase();
      if (otherName && optName && otherName === optName && sameVendor) return true;

      return false;
    });

    if (isDuplicate) {
      flash("err", `"${o.name || o.tourName}" is already added with vendor ${o.vendor || o.source}.`);
      return;
    }

    setRow(key, { source: o.source, tourId: o.tourId, tourName: o.tourName, vendor: o.vendor, variationId: o.variationId, variationTitle: o.variationTitle, cost: o.cost ?? "" });
  };

  // "See all" only: changing a row's country/city clears its tour (catalog differs).
  const setRowCountry = (key, opt) =>
    setRow(key, {
      countryId: opt ? Number(opt.value) : "", countryName: opt?.label || "", cityId: "", cityName: "",
      source: "", tourId: "", tourName: "", vendor: "", variationId: "", variationTitle: "", cost: "",
    });
  const setRowCity = (key, opt) =>
    setRow(key, {
      cityId: opt ? Number(opt.value) : "", cityName: opt?.label || "",
      source: "", tourId: "", tourName: "", vendor: "", variationId: "", variationTitle: "", cost: "",
    });

  const toggleExpand = (key) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, expanded: !r.expanded } : r)));

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.key));
  const toggleSelect = (key) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const toggleSelectAll = () =>
    setSelected(() => (allSelected ? new Set() : new Set(rows.map((r) => r.key))));
  const doDelete = async () => {
    let chosen = rows.filter((r) => selected.has(r.key));
    // Cascade delete: if a parent is chosen, also select its variations
    const chosenParents = chosen.filter(r => !r.variationId && r.tourId);
    if (chosenParents.length > 0) {
      const implicitVariations = rows.filter(r => 
        r.variationId && chosenParents.some(p => p.source === r.source && p.tourId === r.tourId && p.countryId === r.countryId && p.cityId === r.cityId)
      );
      implicitVariations.forEach(v => {
        if (!selected.has(v.key)) {
          chosen.push(v);
          selected.add(v.key); // Ensure it gets dropped from state below
        }
      });
    }
    const persisted = chosen.filter((r) => r.tourId);
    setDeleting(true);
    try {
      if (persisted.length && selUser) {
        // In "see all" the selection can span locations, so delete per (country,
        // city) group; otherwise the whole selection is the current location.
        const calls = [];
        if (seeAll) {
          const groups = new Map();
          for (const r of persisted) {
            const k = `${r.countryId}_${r.cityId}`;
            if (!groups.has(k)) groups.set(k, { userId: selUser.id, countryId: Number(r.countryId), cityId: Number(r.cityId), rows: [] });
            groups.get(k).rows.push({ tourId: r.tourId, variationId: r.variationId });
          }
          groups.forEach((g) => calls.push(g));
        } else {
          calls.push({
            userId: selUser.id,
            countryId: Number(countryId),
            cityId: Number(cityId),
            rows: persisted.map((r) => ({ tourId: r.tourId, variationId: r.variationId })),
          });
        }
        for (const payload of calls) {
          const res = await adminApi("POST", "/admin/users/pricing/delete", payload);
          if (!(res?.data?.statusCode === 200 || res?.status === 200)) {
            flash("err", res?.data?.message || "Delete failed.");
            setDeleting(false);
            return;
          }
        }
      }
      setRows((rs) => {
        const left = rs.filter((r) => !selected.has(r.key));
        return left.length ? left : seeAll ? [] : [blankRow()];
      });
      setSelected(new Set());
      setConfirmDelete(false);
      flash("ok", `Deleted ${chosen.length} product${chosen.length === 1 ? "" : "s"}.`);
    } catch (e) {
      flash("err", "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  // Shared row-level validation (same rules in both modes). Returns an error
  // string or null.
  const validateRows = (valid) => {
    if (!valid.length) return "Add at least one excursion.";
    if (valid.some((r) => !hasVal(r.adultPrice))) return "Adult price is required for every excursion.";
    for (const r of valid) {
      const rErr = firstIncompleteRange(r.dateRanges);
      if (rErr) return rErr;
    }
    if (valid.some((r) => rangesOverlap(r.dateRanges))) return "Date ranges in the same excursion can't overlap.";
    return null;
  };

  const save = async () => {
    if (!selUser) return flash("err", "Select a user first.");
    if (seeAll) return saveAll();
    if (!countryId || !cityId) return flash("err", "Select a country and city.");
    const valid = rows.filter((r) => r.tourId);
    const err = validateRows(valid);
    if (err) return flash("err", err);
    setSaving(true);
    try {
      const res = await adminApi("POST", "/admin/users/pricing", {
        userId: selUser.id,
        username: selUser.username,
        displayName: selUser.displayName,
        userGroup: selUser.userGroup,
        countryId: Number(countryId),
        countryName: selectedCountry?.countryName || "",
        cityId: Number(cityId),
        cityName: selectedCity?.cityName || "",
        rows: valid,
      });
      if (res?.data?.statusCode === 200 || res?.status === 200) {
        flash("ok", `Saved ${res?.data?.data?.saved ?? valid.length} prices.`);
      } else {
        flash("err", res?.data?.message || "Save failed.");
      }
    } catch (e) {
      flash("err", "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // "See all" save: rows span locations for this user. Group by (country, city)
  // and POST each group — safe because "see all" loaded every row for the user,
  // so each location group is complete (server replaces per location).
  const saveAll = async () => {
    const valid = rows.filter((r) => r.tourId);
    if (valid.some((r) => !r.countryId || !r.cityId))
      return flash("err", "Select a country and city for every excursion.");
    const err = validateRows(valid);
    if (err) return flash("err", err);

    const groups = new Map();
    for (const r of valid) {
      const k = `${r.countryId}_${r.cityId}`;
      if (!groups.has(k))
        groups.set(k, {
          userId: selUser.id,
          username: selUser.username,
          displayName: selUser.displayName,
          userGroup: selUser.userGroup,
          countryId: Number(r.countryId),
          countryName: r.countryName || "",
          cityId: Number(r.cityId),
          cityName: r.cityName || "",
          rows: [],
        });
      groups.get(k).rows.push(r);
    }

    setSaving(true);
    try {
      let saved = 0;
      let failed = 0;
      for (const g of groups.values()) {
        const res = await adminApi("POST", "/admin/users/pricing", g);
        if (res?.data?.statusCode === 200 || res?.status === 200) saved += res?.data?.data?.saved ?? g.rows.length;
        else failed += 1;
      }
      if (failed) {
        flash("err", `Saved ${saved} prices; ${failed} location${failed === 1 ? "" : "s"} failed.`);
      } else {
        flash("ok", `Saved ${saved} prices across ${groups.size} location${groups.size === 1 ? "" : "s"}.`);
        await loadPricing(selUser.id, cityId, countryId, true);
      }
    } catch (e) {
      flash("err", "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const groupedRows = useMemo(() => {
    const groupsMap = new Map();
    const result = [];
    
    rows.forEach(r => {
      if (!r.tourId) {
        result.push({ parent: r, variations: [], key: r.key });
      } else {
        const key = `${r.source}::${r.tourId}::${r.countryId}::${r.cityId}`;
        if (!groupsMap.has(key)) {
          const newGroup = { parent: null, variations: [], key };
          groupsMap.set(key, newGroup);
          result.push(newGroup);
        }
        const group = groupsMap.get(key);
        if (!r.variationId) {
          group.parent = r;
        } else {
          group.variations.push(r);
        }
      }
    });
    return result;
  }, [rows]);

  const searchQ = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    let result = groupedRows;
    
    if (vendorFilter) {
      result = result.filter(g => {
        if (g.parent && g.parent.vendor === vendorFilter) return true;
        if (g.variations.some(v => v.vendor === vendorFilter)) return true;
        return false;
      });
    }

    if (searchQ) {
      result = result.filter(g => {
        if (g.parent && `${g.parent.tourName || ""} ${g.parent.variationTitle || ""}`.toLowerCase().includes(searchQ)) return true;
        if (g.variations.some(v => `${v.tourName || ""} ${v.variationTitle || ""}`.toLowerCase().includes(searchQ))) return true;
        return false;
      });
    }

    return result;
  }, [groupedRows, searchQ, vendorFilter]);

  const rowPageCount = Math.max(1, Math.ceil(filteredGroups.length / rowsPerPage));
  const rowSafePage = Math.min(rowPage, rowPageCount);
  const rowStart = (rowSafePage - 1) * rowsPerPage;
  const pagedGroups = filteredGroups.slice(rowStart, rowStart + rowsPerPage);

  const getAvailableExcursions = useCallback(
    (r, baseOpts) => {
      if (!baseOpts || !baseOpts.length) return [];

      const otherRows = rows.filter((other) => {
        if (!other.tourId || other.key === r.key) return false;
        if (r.tourId) {
          const isSameGroup =
            String(other.tourId) === String(r.tourId) &&
            String(other.source || "").toLowerCase() === String(r.source || "").toLowerCase() &&
            (!seeAll ||
              (String(other.countryId ?? "") === String(r.countryId ?? "") &&
                String(other.cityId ?? "") === String(r.cityId ?? "")));
          if (isSameGroup) return false;
        }
        return true;
      });

      return baseOpts.filter((opt) => {
        const isAlreadyAdded = otherRows.some((other) => {
          if (seeAll) {
            if (r.countryId && other.countryId && String(r.countryId) !== String(other.countryId)) return false;
            if (r.cityId && other.cityId && String(r.cityId) !== String(other.cityId)) return false;
          }

          const sameIdAndSource =
            String(other.tourId) === String(opt.tourId) &&
            String(other.source || "").toLowerCase() === String(opt.source || "").toLowerCase();
          if (sameIdAndSource) return true;

          const sameVendor =
            Boolean(other.vendor && opt.vendor && String(other.vendor).trim().toLowerCase() === String(opt.vendor).trim().toLowerCase());
          const sameTourId = String(other.tourId) === String(opt.tourId);
          if (sameTourId && sameVendor) return true;

          const otherName = (other.tourName || "").trim().toLowerCase();
          const optName = (opt.tourName || opt.name || "").trim().toLowerCase();
          if (otherName && optName && otherName === optName && sameVendor) return true;

          return false;
        });

        return !isAlreadyAdded;
      });
    },
    [rows, seeAll]
  );

  const getAvailableVariations = (g, currentRowId) => {
    const ref = g.parent || g.variations[0];
    if (!ref || !ref.tourId) return [];
    
    const doc = catalog.find((c) => String(c.source) === String(ref.source) && String(c.tourId) === String(ref.tourId));
    if (!doc) return [];

    const existingVarIds = new Set(g.variations.map(v => v.variationId));
    if (currentRowId) existingVarIds.delete(String(currentRowId));
    
    return (doc.variations || [])
      .filter((v) => !isWithoutTransfer(v.variation_title))
      .filter((v) => !existingVarIds.has(String(v.variation_unique_id)))
      .map((v) => ({
        source: ref.source,
        tourId: ref.tourId,
        tourName: ref.tourName,
        vendor: ref.vendor,
        variationId: String(v.variation_unique_id),
        variationTitle: v.variation_title,
        cost: v.originalAdult ?? (doc.vendorCost ?? ""),
        name: v.variation_title,
        isVariation: true,
        value: rowValue(ref.source, ref.tourId, String(v.variation_unique_id)),
        label: `${v.variation_title}`,
      }));
  };

  const addVariationRow = (opt, g) => {
    if (!opt) return;
    const ref = g.parent || g.variations[0];
    const newRow = {
      ...blankRow(),
      source: opt.source,
      tourId: opt.tourId,
      tourName: opt.tourName,
      vendor: opt.vendor,
      variationId: opt.variationId,
      variationTitle: opt.variationTitle,
      cost: opt.cost ?? "",
      countryId: ref.countryId,
      countryName: ref.countryName,
      cityId: ref.cityId,
      cityName: ref.cityName
    };
    
    const groupRows = [g.parent, ...g.variations].filter(Boolean);
    const lastRowInGroup = groupRows[groupRows.length - 1];
    
    setRows(rs => {
      const idx = rs.findIndex(r => r.key === lastRowInGroup.key);
      if (idx >= 0) {
        const next = [...rs];
        next.splice(idx + 1, 0, newRow);
        return next;
      }
      return [...rs, newRow];
    });
    setAddingVariationFor(null);
  };

  const addExcursion = () => {
    const row = blankRow();
    setRows((rs) => [...rs, row]);
    setRowPage(Math.max(1, Math.ceil((rows.length + 1) / rowsPerPage)));
    setPendingScrollKey(row.key);
  };

  const rowPager =
    filteredGroups.length > rowsPerPage ? (
      <div className="up-pager">
        <button className="up-pg-btn" disabled={rowSafePage <= 1} onClick={() => setRowPage((p) => Math.max(1, p - 1))}>
          ‹ Prev
        </button>
        <span className="up-pg-info">
          Page {rowSafePage} of {rowPageCount} · {rowStart + 1}–{Math.min(rowStart + rowsPerPage, filteredGroups.length)} of {filteredGroups.length}
        </span>
        <button className="up-pg-btn" disabled={rowSafePage >= rowPageCount} onClick={() => setRowPage((p) => Math.min(rowPageCount, p + 1))}>
          Next ›
        </button>
      </div>
    ) : null;

  return (
    <div className="up">

      {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}
      <div id="rdp-portal" />

      {confirmDelete ? (
        <div className="gp-modal-ov" onClick={() => (deleting ? null : setConfirmDelete(false))}>
          <div className="gp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gp-modal-t">Delete {selected.size} product{selected.size === 1 ? "" : "s"}?</div>
            <div className="gp-modal-s">
              This permanently removes the selected pricing {selected.size === 1 ? "row" : "rows"} for{" "}
              {selUser?.displayName || selUser?.username || "this user"}{seeAll ? " across their locations" : ""}. This can’t be undone.
            </div>
            <div className="gp-modal-a">
              <button className="btn soft" disabled={deleting} onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn danger" disabled={deleting} onClick={doDelete}>
                {deleting ? <span className="spin dark" /> : null} {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {view === "list" ? (
        <>
          <h1 className="up-title">User Pricing</h1>
          <p className="up-sub">Per-user overrides. User prices take priority over group and global.</p>

          <div className="card up-listcard">
            <div className="up-toolbar">
              <span className="up-count">{users.length} users</span>
              <div className="up-toolbar-r">
                <input
                  className="up-toolbar-search"
                  placeholder="Search users…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <button className="btn soft sm" disabled={syncing} onClick={syncUsers}>
                  {syncing ? <span className="spin dark" /> : <FaSync size={13} style={{ verticalAlign: "middle" }} />} {syncing ? "Syncing…" : "Sync"}
                </button>
              </div>
            </div>

            <div className="up-utablewrap">
              <table className="t up-utable">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>Agency / Name</th>
                    <th>Email</th>
                    <th style={{ width: 150 }}>Username</th>
                    <th style={{ width: 140 }}>Group</th>
                    <th style={{ width: 96 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6}><div className="hint">Loading…</div></td></tr>
                  ) : pagedUsers.length ? (
                    pagedUsers.map((u, i) => (
                      <tr key={u.id} className="up-urow" onClick={() => openEdit(u)}>
                        <td style={{ color: "#98a2b6" }}>{(safePage - 1) * USERS_PER_PAGE + i + 1}</td>
                        <td className="up-uname">{u.displayName || u.username}</td>
                        <td className="up-uemail">{u.email || "—"}</td>
                        <td className="up-umuted">{u.username || "—"}</td>
                        <td>{u.userGroup ? <span className="grp">{u.userGroup}</span> : <span style={{ color: "#c0c7d4" }}>—</span>}</td>
                        <td>
                          <button
                            className="btn soft sm"
                            onClick={(e) => { e.stopPropagation(); openEdit(u); }}
                          >
                            ✎ Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={6}><div className="hint">No users found.</div></td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {!loading && filteredUsers.length > USERS_PER_PAGE ? (
              <div className="up-pager">
                <button
                  className="up-pg-btn"
                  disabled={safePage <= 1}
                  onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                >
                  ‹ Prev
                </button>
                <span className="up-pg-info">
                  Page {safePage} of {userPageCount} · {(safePage - 1) * USERS_PER_PAGE + 1}–
                  {Math.min(safePage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length}
                </span>
                <button
                  className="up-pg-btn"
                  disabled={safePage >= userPageCount}
                  onClick={() => setUserPage((p) => Math.min(userPageCount, p + 1))}
                >
                  Next ›
                </button>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {view === "edit" && selUser ? (
        <>
          <button className="up-back" onClick={backToList}><ArrowLeft size={16} /> Back to users</button>

          <div className="card">
            <div className="det">
              <div className="det-head">
                <div>
                  <h2 className="det-name">{selUser.displayName || selUser.username}</h2>
                  <div className="det-meta">
                    <span>{selUser.email}</span>
                    <span>· {selUser.username}</span>
                    {selUser.userGroup ? <span className="grp">Group: {selUser.userGroup}</span> : null}
                    {(selUser.roles || []).length ? <span>· {selUser.roles.join(", ")}</span> : null}
                  </div>
                </div>
              </div>

              <div className="loc">
                <div className="fld">
                  <label>Country</label>
                  <Select
                    options={countries.map((c) => ({ value: String(c.countryId), label: c.countryName }))}
                    value={countryId ? { value: String(countryId), label: selectedCountry?.countryName || "" } : null}
                    onChange={(opt) => { setCountryId(opt?.value || ""); setCityId(""); }}
                    placeholder="Select country"
                    isClearable
                    isDisabled={seeAll}
                    components={rsComponents}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    styles={locSelectStyles}
                  />
                </div>
                <div className="fld">
                  <label>City</label>
                  <Select
                    options={cityOptions.map((c) => ({ value: String(c.cityId), label: c.cityName }))}
                    value={cityId ? { value: String(cityId), label: selectedCity?.cityName || "" } : null}
                    onChange={(opt) => setCityId(opt?.value || "")}
                    isDisabled={!countryId || seeAll}
                    placeholder="Select city"
                    isClearable
                    components={rsComponents}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    styles={locSelectStyles}
                  />
                </div>
                <label className="gp-allchk">
                  <input type="checkbox" checked={seeAll} onChange={(e) => setSeeAll(e.target.checked)} />
                  See all countries &amp; cities
                </label>
              </div>

              <div className="gp-actions top">
                <div className="gp-actions-l">
                  <button className="btn soft" onClick={addExcursion}>＋ Add Excursion</button>
                  <input
                    className="inp"
                    placeholder="Search Excursion…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setRowPage(1); }}
                    style={{ height: 42, minWidth: 200, fontSize: 13, boxSizing: "border-box" }}
                  />
                  <div style={{ minWidth: 200 }}>
                    <Select
                      options={[
                        { value: "Rayna", label: "Rayna" },
                        { value: "Parmar", label: "Parmar" },
                        { value: "DubaiPark", label: "DubaiPark" },
                        { value: "Dahrtours", label: "Dahrtours" },
                        { value: "Emaar", label: "Emaar" },
                        { value: "HeliDubai", label: "HeliDubai" },
                      ]}
                      value={vendorFilter ? { value: vendorFilter, label: vendorFilter } : null}
                      onChange={(opt) => { setVendorFilter(opt ? opt.value : ""); setRowPage(1); }}
                      placeholder="All Vendors"
                      isClearable
                      components={rsComponents}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={selectStyles}
                    />
                  </div>
                  {selected.size > 0 ? (
                    <button className="btn danger" onClick={() => setConfirmDelete(true)}>
                      🗑 Delete selected ({selected.size})
                    </button>
                  ) : null}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <div className="fld" style={{ minWidth: "10px" }}>
                    <Select
                      options={rowsOptions.map((n) => ({ value: String(n), label: `${n} rows` }))}
                      value={rowsPerPage ? { value: String(rowsPerPage), label: `${rowsPerPage} rows` } : null}
                      onChange={(opt) => setRowsPerPage(opt?.value || "")}
                      placeholder="Select rows"
                      components={rsComponents}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={locSelectStyles}
                    />
                  </div>
                  <button className="btn primary" disabled={saving} onClick={save}>
                    {saving ? <span className="spin" /> : null} {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
              {seeAll ? (
                <div className="gp-allnote gp-allnote-edit">
                  Editing all countries &amp; cities for this user — each row keeps its own location, and changes save per country/city. {rows.length} rows.
                </div>
              ) : null}
              <div className="up-tablewrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th style={{ width: 34 }}>
                        <input type="checkbox" className="chk" checked={allSelected} onChange={toggleSelectAll} title="Select all" />
                      </th>
                      <th style={{ width: 40 }}>#</th>
                      {seeAll ? <th style={{ width: 150 }}>Country</th> : null}
                      {seeAll ? <th style={{ width: 140 }}>City</th> : null}
                      <th>Excursion Tour / Variation</th>
                      <th style={{ width: 90 }}>Vendor</th>
                      <th style={{ width: 70 }} title="Show this product ahead in the storefront">Priority</th>
                      <th style={{ width: 84 }}>Cost</th>
                      {PRICE_COLS.map((c) => <th key={c.key}>{c.label}</th>)}
                      <th style={{ width: 88 }}>Dates</th>
                      <th style={{ width: 76 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedGroups.map((g, gIndex) => {
                      const isExpanded = expandedGroups.has(g.key);
                      const groupRows = [];
                      if (g.parent) groupRows.push({ r: g.parent, isVar: false });
                      if (isExpanded) {
                        g.variations.forEach(v => groupRows.push({ r: v, isVar: true }));
                      }
                      
                      const availableVars = getAvailableVariations(g);
                      const displayIndex = rowStart + gIndex + 1;

                      return (
                        <React.Fragment key={g.key}>
                          {groupRows.map(({ r, isVar }, idx) => {
                            const gi = rows.indexOf(r);
                            const baseOpts = seeAll ? buildOptions(r.countryId, r.cityId) : options;
                            const rowOpts = getAvailableExcursions(r, baseOpts);
                            const rowCities = countries.find((c) => String(c.countryId) === String(r.countryId))?.cities || [];
                            const locReady = !seeAll || (r.countryId && r.cityId);
                            const isLastInGroup = idx === groupRows.length - 1;

                            return (
                              <React.Fragment key={r.key}>
                                <tr ref={r.key === pendingScrollKey ? pendingRowRef : null} className={selected.has(r.key) ? "row-sel" : ""}>
                                  <td>
                                    <input type="checkbox" className="chk" checked={selected.has(r.key)} onChange={() => toggleSelect(r.key)} />
                                  </td>
                                  <td style={{ color: "#98a2b6" }}>{!isVar ? displayIndex : ""}</td>
                                  {seeAll ? (
                                    <td>
                                      <Select
                                        options={countries.map((c) => ({ value: String(c.countryId), label: c.countryName }))}
                                        value={r.countryId ? { value: String(r.countryId), label: r.countryName || "" } : null}
                                        onChange={(opt) => setRowCountry(r.key, opt)}
                                        placeholder="Country"
                                        isClearable
                                        components={rsComponents}
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                        styles={selectStyles}
                                        isDisabled={isVar}
                                      />
                                    </td>
                                  ) : null}
                                  {seeAll ? (
                                    <td>
                                      <Select
                                        options={rowCities.map((c) => ({ value: String(c.cityId), label: c.cityName }))}
                                        value={r.cityId ? { value: String(r.cityId), label: r.cityName || "" } : null}
                                        onChange={(opt) => setRowCity(r.key, opt)}
                                        isDisabled={!r.countryId || isVar}
                                        placeholder="City"
                                        isClearable
                                        components={rsComponents}
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                        styles={selectStyles}
                                      />
                                    </td>
                                  ) : null}
                                  <td style={{ paddingLeft: isVar ? "24px" : "8px" }}>
                                    {isVar ? (
                                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <div style={{ flex: 1 }}>
                                          <Select
                                            options={getAvailableVariations(g, r.variationId)}
                                            value={rowTourValue(r)}
                                            onChange={(opt) => {
                                              if (opt) setRow(r.key, { variationId: opt.variationId, variationTitle: opt.variationTitle, cost: opt.cost ?? "" });
                                            }}
                                            formatOptionLabel={OptionLabel}
                                            placeholder="Select Variation…"
                                            isClearable={false}
                                            components={rsComponents}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            styles={selectStyles}
                                          />
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <button
                                          className="btn"
                                          style={{ padding: "0 4px", color: "#000",background:"transparent", height: "auto", minHeight: "auto", visibility: (g.variations.length > 0 || availableVars.length > 0) ? "visible" : "hidden" }}
                                          onClick={() => toggleGroupCollapse(g.key)}
                                        >
                                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                        </button>
                                        <div style={{ flex: 1 }}>
                                          <Select
                                            options={rowOpts}
                                            value={rowTourValue(r)}
                                            onChange={(opt) => onSelectTour(r.key, opt || null)}
                                            formatOptionLabel={OptionLabel}
                                            placeholder={locReady ? "Search excursion…" : "Pick a city first"}
                                            isDisabled={!locReady}
                                            isClearable
                                            components={rsComponents}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            styles={selectStyles}
                                            noOptionsMessage={({ inputValue }) => {
                                              const q = (inputValue || "").trim().toLowerCase();
                                              if (q) {
                                                const match = (o) =>
                                                  String(o?.label || "").toLowerCase().includes(q) ||
                                                  String(o?.name || o?.tourName || "").toLowerCase().includes(q);
                                                if (!rowOpts.some(match) && baseOpts.some(match)) {
                                                  return "Already added";
                                                }
                                              }
                                              return "No options";
                                            }}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {r.vendor ? (
                                      <span className={`vend ${r.source === "rayna" ? "rayna" : ""}`}>{r.vendor}</span>
                                    ) : (
                                      <span style={{ color: "#c0c7d4" }}>—</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {r.tourId && !isVar ? (
                                      <input
                                        type="checkbox"
                                        className="chk"
                                        checked={priorityMap.has(prKey(r))}
                                        onChange={() => togglePriority(r)}
                                        title="Show ahead in storefront"
                                      />
                                    ) : (
                                      <span style={{ color: "#c0c7d4" }}>—</span>
                                    )}
                                  </td>
                                  <td>{renderCostAC(r)}</td>
                                  {PRICE_COLS.map((c) => (
                                    <td key={c.key}>
                                      <input
                                        className="inp price"
                                        type="number"
                                        min={0}
                                        value={r[c.key]}
                                        onChange={(e) => setRow(r.key, { [c.key]: nn(e.target.value) })}
                                      />
                                    </td>
                                  ))}
                                  <td>
                                    <button
                                      className={`dates-btn${(r.dateRanges || []).some((d) => d.startDate) ? " on" : ""}`}
                                      onClick={() => toggleExpand(r.key)}
                                    >
                                      <CalendarDays size={13} /> Dates
                                    </button>
                                  </td>
                                  <td>
                                    <div className="row-acts">
                                      <button className="row-act del" title="Delete row" onClick={() => deleteRow(r)}><Trash2 size={15} /></button>
                                      <button className="row-act add" title="Add row below" onClick={() => isVar ? addVariationRowAfter(gi, r) : addRowAfter(gi)}><Plus size={15} /></button>
                                    </div>
                                  </td>
                                </tr>
                                {r.expanded ? (
                                  <tr className="up-subrow">
                                    <td />
                                    <td />
                                    <td colSpan={seeAll ? 14 : 12}>
                                      <DateRangesEditor
                                        ranges={r.dateRanges || []}
                                        onChange={(next) => setRow(r.key, { dateRanges: next })}
                                        hint="overrides this user's price for travel dates in each range"
                                      />
                                    </td>
                                  </tr>
                                ) : null}
                                {isLastInGroup && g.parent && availableVars.length > 0 && isExpanded && (
                                  <tr>
                                    <td />
                                    <td />
                                    {seeAll ? <><td /><td /></> : null}
                                    <td colSpan={12} style={{ padding: "4px 8px 12px 8px" }}>
                                      <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px"
                                      }}>
                                        {addingVariationFor === g.key ? (
                                          <div style={{ width: 300 }}>
                                            <Select
                                              options={availableVars}
                                              onChange={(opt) => addVariationRow(opt, g)}
                                              placeholder="Select Variation…"
                                              autoFocus
                                              components={rsComponents}
                                              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                              styles={selectStyles}
                                            />
                                          </div>
                                        ) : (
                                          <button 
                                            className="btn soft" 
                                            onClick={() => setAddingVariationFor(g.key)} 
                                            style={{ 
                                              color: "#3264FF", 
                                              fontWeight: 600,
                                              backgroundColor: "#f4f7fc",
                                              border: "none",
                                              boxShadow: "none"
                                            }}
                                          >
                                            + Add Variation
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rowPager}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default UserPricing;
